import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import authServiceV2 from '@/shared/api/authServiceV2';
import { authApi } from '../entities/auth';

const AuthContext = createContext<any>(null);

export const AuthProvider = ({ children }: any) => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  /**
   * One-time cleanup of legacy localStorage tokens
   */
  useEffect(() => {
    const cleaned = localStorage.getItem('_auth_legacy_cleaned');
    if (!cleaned) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      localStorage.setItem('_auth_legacy_cleaned', '1');
    }
  }, []);

  /**
   * Logout user — calls backend to clear cookies
   */
  const logout = useCallback(async () => {
    await authServiceV2.logout();
    setUser(null);
  }, []);

  /**
   * Initialize auth state on mount — check cookie session via /me
   */
  useEffect(() => {
    const initAuth = async () => {
      try {
        const result = await authServiceV2.getCurrentUser();
        if (result.success && result.user) {
          setUser(result.user);
        }
      } catch (error: any) {
        console.error('Auth initialization error:', error);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    const handleSessionExpired = () => {
      logout();
      window.location.href = '/login?sessionExpired=true';
    };

    const handleLogout = () => {
      setUser(null);
    };

    window.addEventListener('auth:session-expired', handleSessionExpired);
    window.addEventListener('auth:logout', handleLogout);

    return () => {
      window.removeEventListener('auth:session-expired', handleSessionExpired);
      window.removeEventListener('auth:logout', handleLogout);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Login user via cookie auth
   */
  const login = async (email: any, password: any) => {
    try {
      const result = await authServiceV2.login(email, password);

      if (result.success) {
        setUser(result.user);
        return { success: true, user: result.user };
      }

      return {
        success: false,
        error: result.error || 'Erreur de connexion',
      };
    } catch (error: any) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.error || 'Erreur de connexion',
      };
    }
  };

  /**
   * Register new user
   */
  const register = async (userData: any) => {
    try {
      await authApi.register(userData);
      return await login(userData.email, userData.password);
    } catch (error: any) {
      console.error('Register error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || "Erreur lors de l'inscription",
      };
    }
  };

  /**
   * Set user directly (used by OAuth callbacks)
   */
  const setUserDirectly = (userData: any) => {
    setUser(userData);
  };

  /**
   * Refresh user data from server
   */
  const refreshUser = async () => {
    try {
      const result = await authServiceV2.getCurrentUser();
      if (result.success) {
        setUser(result.user);
        return result.user;
      }
      throw new Error(result.error);
    } catch (error: any) {
      console.error('Error refreshing user:', error);
      if (error.response?.status === 401) {
        logout();
      }
      throw error;
    }
  };

  const isAuthenticated = useCallback(() => !!user, [user]);

  const hasRole = useCallback(
    (allowedRoles: any) => {
      if (!user) return false;
      const roles = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];
      const userRole = user.role?.toUpperCase();
      return roles.some((role) => role.toUpperCase() === userRole);
    },
    [user]
  );

  const getRole = useCallback(() => user?.role?.toUpperCase() || null, [user]);

  const isAdmin = useCallback(() => hasRole('ADMIN'), [hasRole]);
  const isSeller = useCallback(() => hasRole(['SELLER', 'ADMIN']), [hasRole]);
  const isProfessionnel = useCallback(() => hasRole(['PROFESSIONNEL', 'ADMIN']), [hasRole]);

  const getDefaultDashboard = useCallback(() => {
    const role = getRole();
    switch (role) {
      case 'ADMIN':
        return '/admin/dashboard';
      case 'SELLER':
        return '/seller/dashboard';
      case 'PROFESSIONNEL':
        return '/pro/dashboard';
      default:
        return '/client/dashboard';
    }
  }, [getRole]);

  const value = {
    user,
    loading,

    login,
    register,
    logout,
    setUser: setUserDirectly,
    refreshUser,

    isAuthenticated,
    hasRole,
    getRole,
    isAdmin,
    isSeller,
    isProfessionnel,
    getDefaultDashboard,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
